import React from 'react'
import { Dialog, DialogContent, Box, Typography } from '@mui/material'
import { Announcement } from '@/src/modules/announcements/types/announcementTypes'
import DeploymentCountdown from './DeploymentCountdown'

interface DeploymentModalProps {
  announcement: Announcement
  open: boolean
}

const DeploymentModal: React.FC<DeploymentModalProps> = ({ announcement, open }) => {
  const [progress, setProgress] = React.useState(0)

  // Calculate progress percentage between start and end dates
  const calculateProgress = () => {
    if (!announcement.startDate || !announcement.endDate) return 0

    const now = new Date().getTime()
    const start = new Date(announcement.startDate).getTime()
    const end = new Date(announcement.endDate).getTime()

    // If we haven't started yet, show 0%
    if (now < start) return 0

    // If we're past the end date, show 100%
    if (now > end) return 100

    // Calculate percentage of time elapsed between start and end
    const totalDuration = end - start
    const elapsed = now - start
    const progress = (elapsed / totalDuration) * 100

    return Math.max(0, Math.min(100, progress))
  }

  // Update progress every second for real-time updates
  React.useEffect(() => {
    if (!announcement.startDate || !announcement.endDate) return

    // Calculate initial progress
    setProgress(calculateProgress())

    // Update progress every second
    const interval = setInterval(() => {
      setProgress(calculateProgress())
    }, 1000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcement.startDate, announcement.endDate])

  // Check if we have an end date for progress tracking
  const hasEndDate = Boolean(announcement.endDate)

  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      disableEscapeKeyDown
      onClose={() => undefined} // Prevent closing on backdrop click
      PaperProps={{
        sx: {
          backgroundColor: '#ffffff',
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          border: 'none',
          position: 'relative',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ p: 4, textAlign: 'center' }}>
        {/* Rocket Icon */}
        <Box sx={{ mb: 3 }}>
          <span style={{ fontSize: '48px' }}>🚀</span>
        </Box>

        {/* Title */}
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: '#2c3e50',
            mb: 2
          }}
        >
          {announcement.title}
        </Typography>

        {/* Divider */}
        <Box
          sx={{
            width: '60px',
            height: '3px',
            backgroundColor: '#ecf0f1',
            borderRadius: '2px',
            mx: 'auto',
            mb: 3
          }}
        />

        {/* Description */}
        <Typography
          variant='body1'
          sx={{
            color: '#34495e',
            mb: 4,
            fontSize: '1.1rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap'
          }}
        >
          {announcement.message}
        </Typography>

        {/* Countdown Timer - Only show when endDate exists */}
        {hasEndDate && (
          <Box sx={{ mb: 4 }}>
            <DeploymentCountdown
              endDate={announcement.endDate!}
              onComplete={() => {
                // Optional: Handle countdown completion
              }}
            />
          </Box>
        )}

        {/* Progress Bar Section - Only show when endDate exists */}
        {hasEndDate && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography
                variant='body2'
                sx={{
                  color: '#2c3e50',
                  fontWeight: 500
                }}
              >
                Deployment Progress...
              </Typography>
              <Typography
                variant='body2'
                sx={{
                  color: '#2c3e50',
                  fontWeight: 600
                }}
              >
                {Math.round(progress)}%
              </Typography>
            </Box>

            {/* Progress Bar */}
            <Box
              sx={{
                width: '100%',
                height: 8,
                backgroundColor: '#ecf0f1',
                borderRadius: 4,
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  backgroundColor: '#2c3e50',
                  borderRadius: 4,
                  transition: 'width 0.5s ease-in-out',
                  width: `${progress}%`
                }}
              />
            </Box>
          </Box>
        )}

        {/* Progress Note - Different message based on endDate existence */}
        <Typography
          variant='caption'
          sx={{
            color: '#7f8c8d',
            display: 'block',
            mb: 3,
            backgroundColor: '#e3f2fd',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '0.875rem'
          }}
        >
          {hasEndDate
            ? 'You can continue once this process is complete.'
            : 'This message will disappear when the process is complete.'}
        </Typography>

        {/* Team Message */}
        <Typography
          variant='body2'
          sx={{
            color: '#2c3e50',
            textAlign: 'center',
            fontStyle: 'italic',
            mt: 2
          }}
        >
          Thanks for your patience! – Resto365 Team
        </Typography>
      </DialogContent>
    </Dialog>
  )
}

export default DeploymentModal
